import { useContext } from 'react'

import { Button, ModalContext, SidebarModal } from '@domain-group/suit-theme'

import { formatEntityCount } from '@functions/entity'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { ActionResult } from '@redux/modules/types'
import type { AgencyModel } from '@models/types'

import CustomSizeForm from './components/CustomSizeForm'
import DownloadReady from './components/DownloadReady'
import PresetImageSizes from './components/PresetImageSizes'
import ProcessingMessage from './components/ProcessingMessage'

import useDownloadMedia from './hooks/useDownloadMedia'

const MODAL_KEY = 'DownloadMediaModal'

interface ModalCallbacks {
  closeModal: () => void,
  downloadImages: (payload: HandleCallbackActionParams) => Promise<ActionResult<{ queued: boolean }>>,
  updateAgency: (payload: HandleCallbackActionParams) => Promise<ActionResult<AgencyModel>>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  isPublicPage?: boolean,
  showModal: boolean,
}

const DownloadMediaModal = (props: ModalProps) => {
  const { callbacks, isPublicPage, showModal } = props
  const { downloadImages, updateAgency } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const { campaign, selectedImageIds = [] } = modalData[MODAL_KEY] || {}

  const downloadMediaPayload = useDownloadMedia(campaign)
  const {
    callbacks: { downloadImages: downloadFn, setState },
    downloadLink,
    downloadReady,
    processingImages,
    publicLinkFontColor,
    publicLinkTextAccentColour,
    selectedPreset,
    showCustomSizeForm,
    timer,
    updatingCampaign,
  } = downloadMediaPayload

  const downloadImagesPayload = {
    callbacks: {
      action: downloadFn,
    },
    entityParams: {
      image_ids: selectedImageIds,
      public_token: isPublicPage ? campaign.public_token : undefined,
      resize: selectedPreset.type,
      skip_watermark: !downloadMediaPayload.watermarkImagesEnabled,
    },
    toastText: 'Images Queued for Download',
  }

  const publicLinkButtonStyle = (!!publicLinkFontColor && !!publicLinkTextAccentColour) ? {
    backgroundColor: publicLinkTextAccentColour,
    borderColor: publicLinkTextAccentColour,
    color: publicLinkFontColor,
  } : {}

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Download" titleSecondLine="Media" />

      <SidebarModal.Content>
        {processingImages && !downloadReady && <ProcessingMessage timer={timer} />}

        {!processingImages && !downloadReady && (
          <PresetImageSizes downloadMediaPayload={downloadMediaPayload} isPublicPage={isPublicPage} />
        )}

        {showCustomSizeForm && !processingImages && !downloadReady && (
          <CustomSizeForm callbacks={{ updateAgency }} downloadMediaPayload={downloadMediaPayload} />
        )}

        {downloadReady && (
          <DownloadReady downloadLink={downloadLink} />
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        {downloadReady && (
          <Button
            // @ts-expect-error TS2322
            as="a"
            buttonStyle="primaryCreate"
            href={downloadLink}
            size="large"
            target="_blank"
            style={publicLinkButtonStyle}
          >
            Download Images
          </Button>
        )}

        {!downloadReady && (
          <Button
            buttonStyle="primaryEdit"
            loading={updatingCampaign}
            onClick={() => {
              setState({ processingImages: true })
              // @ts-expect-error TS2345
              return downloadImages(downloadImagesPayload)
            }}
            size="large"
            style={publicLinkButtonStyle}
          >
            {`Start Download (${formatEntityCount({ entityCount: selectedImageIds.length, entityString: 'Image' })})`}
          </Button>
        )}
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <DownloadMediaModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
